import { trace, SpanStatusCode, Span } from '@opentelemetry/api';

const tracer = trace.getTracer('blackmirror-demo');

type SpanAttributes = Record<string, string | number | boolean>;

/**
 * Generic traced wrapper for any async operation
 */
export async function traced<T>(
  spanName: string,
  fn: (span: Span) => Promise<T>,
  attributes?: SpanAttributes
): Promise<T> {
  return tracer.startActiveSpan(spanName, async (span) => {
    if (attributes) {
      for (const [key, value] of Object.entries(attributes)) {
        span.setAttribute(key, value);
      }
    }

    try {
      const result = await fn(span);
      span.setStatus({ code: SpanStatusCode.OK });
      return result;
    } catch (error) {
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error instanceof Error ? error.message : String(error)
      });
      throw error;
    } finally {
      span.end();
    }
  });
}

/**
 * Traced fetch wrapper
 */
export async function tracedFetch(
  spanName: string,
  url: string,
  options?: RequestInit
): Promise<Response> {
  return traced(
    spanName,
    async (span) => {
      const response = await fetch(url, options);
      span.setAttribute('http.status_code', response.status);
      return response;
    },
    {
      'http.url': url,
      'http.method': options?.method || 'GET',
    }
  );
}
